import { EyeIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Ui/Button';
import auth from '@/helpers/auth';
import dayjs from '@/helpers/dayjs';
import { useStopImpersonation } from '@/hooks/useImpersonation';

/**
 * Always-visible notice while an administrator acts as another user. The
 * server ends the session at `expires_at`; reloading then brings the
 * administrator back without any polling.
 */
export function ImpersonationBanner() {
    const [
        t,
    ] = useTranslation('pages');

    const stopImpersonation = useStopImpersonation();
    const impersonation = auth.getImpersonation();
    const user = auth.getUser();

    useEffect(() => {
        if (!impersonation) {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            window.location.assign('/');
        }, Math.max(dayjs(impersonation.expires_at).diff(dayjs()), 0) + 1000);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [
        impersonation,
    ]);

    if (!impersonation) {
        return null;
    }

    const isFull = impersonation.mode === 'full';

    return (
        <div
            className={ clsx(
                'sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-2 text-sm',
                isFull ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'
            ) }
            role='status'
        >
            <EyeIcon className='h-4 w-4 shrink-0' />
            <span>
                { t('impersonation.banner', {
                    name: `${ user?.first_name ?? '' } ${ user?.last_name ?? '' }`.trim() || user?.email,
                    company: String(auth.getCompany()?.name ?? ''),
                }) }
            </span>
            <span className='font-semibold'>
                { isFull ? t('impersonation.mode_full') : t('impersonation.mode_read_only') }
            </span>
            <span>{ t('impersonation.expires_at', { time: dayjs(impersonation.expires_at).format('HH:mm') }) }</span>
            <Button
                disabled={ stopImpersonation.isPending }
                onClick={ () => {
                    stopImpersonation.mutate();
                } }
                size='sm'
                variant='secondary'
            >
                { t('impersonation.stop') }
            </Button>
        </div>
    );
}
